import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  ClipboardCheckIcon,
  DocumentIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '../../icons';

/** 메뉴 항목 아이콘 공통 크기 — 라벨(text-sm) 옆 정렬 기준. */
const MENU_ICON_CLASS = 'h-4 w-4 flex-shrink-0';

/**
 * 트리거 기본 결 — 사진 카드·모바일 카드에서 쓰던 값 그대로다. 표 행처럼 목업의 행 액션
 * 규격(`ROW_ACTION_TRIGGER_CLASS`)을 따라야 하는 자리는 `triggerClassName`으로 갈아끼운다.
 * 기본값을 그쪽으로 바꾸지 않는 이유: 30px 고정 정사각이라 카드 안에서는 여백이 어긋난다.
 */
const DEFAULT_TRIGGER_CLASS =
  'rounded text-ink-500 transition-colors hover:bg-ink-200 hover:text-ink-700';

interface MenuItem {
  id: string;
  label: string;
  /** SVG 아이콘 노드. 글꼴 글리프/이모지 금지(크기 클래스가 먹지 않는다). */
  icon?: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  divider?: boolean;
}

interface DropdownMenuProps {
  /** 메뉴 항목 */
  items: MenuItem[];
  /** 트리거 버튼 렌더 */
  trigger?: React.ReactNode;
  /** 정렬 위치 */
  align?: 'left' | 'right';
  /** 버튼 크기 */
  size?: 'sm' | 'md';
  /** 트리거 결 교체(넘기면 `size`의 여백도 이 클래스가 소유한다). */
  triggerClassName?: string;
}

export function DropdownMenu({
  items,
  trigger,
  align = 'right',
  size = 'md',
  triggerClassName,
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 메뉴 위치 계산
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 160;

      setMenuPosition({
        top: rect.bottom + 4,
        left: align === 'right' ? rect.right - menuWidth : rect.left,
      });
    }
  }, [isOpen, align]);

  // 외부 클릭 감지
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // ESC 키로 닫기
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // 스크롤 시 닫기
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const handleItemClick = (item: MenuItem) => {
    if (item.disabled) return;
    item.onClick();
    setIsOpen(false);
  };

  const buttonSizeClass = size === 'sm' ? 'p-1 text-sm' : 'p-2';

  return (
    <div className="relative inline-block">
      {/* 트리거 버튼 */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={triggerClassName ?? `${DEFAULT_TRIGGER_CLASS} ${buttonSizeClass}`}
      >
        {trigger || <EllipsisHorizontalIcon className="h-5 w-5" />}
      </button>

      {/* 드롭다운 메뉴 (Portal로 body에 렌더링) */}
      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
          }}
          className="z-[9999] py-1 bg-white rounded-card shadow-card border border-line min-w-[160px] animate-fade-in"
        >
          {items.map((item, index) => (
            <React.Fragment key={item.id}>
              {item.divider && index > 0 && (
                <div className="h-px bg-ink-100 my-1" />
              )}
              <div className="relative group">
                <button
                  type="button"
                  onClick={() => handleItemClick(item)}
                  disabled={item.disabled}
                  className={`
                    w-full px-4 py-2 text-sm text-left flex items-center gap-2
                    transition-colors
                    ${item.disabled
                      ? 'text-ink-300 cursor-not-allowed'
                      : item.danger
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-ink-700 hover:bg-surface-hover'
                    }
                  `}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
                {item.disabled && item.disabledReason && (
                  <div className="absolute right-full top-1/2 -translate-y-1/2 mr-2 px-2 py-1 text-xs text-white bg-ink-800 rounded-card shadow-card whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[10000]">
                    {item.disabledReason}
                    <div className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-y-4 border-y-transparent border-l-4 border-l-ink-800" />
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * 점검 카드용 드롭다운 메뉴 (미리 정의된 메뉴)
 */
interface InspectionDropdownMenuProps {
  /** 점검 ID (향후 사용을 위해 예약) */
  inspectionId?: string;
  status?: string;
  /** 삭제된 점검 여부 */
  isDeleted?: boolean;
  /** 삭제/복원 권한 여부 */
  canDelete?: boolean;
  /** 이후 차수 점검이 존재하여 삭제 불가 여부 */
  isLockedByLaterInspection?: boolean;
  /** 검토 요청 권한 여부 */
  canRequestReview?: boolean;
  /**
   * 보고서 생성 **권한** 여부 — 호출부가 계산한다.
   *
   * ⚠️⚠️ **오늘의 소유자는 이 prop이 아니라 `reportGate`다**(2026-10-09 `a8061c0c4`부터 서버가
   * `canCreateReport`를 집행 식으로 계산해 싣는다). 유일한 운영 소비처인 `InspectionTable`은
   * **`true`를 넘긴다** — 그 자리에서 권한을 AND하면 집행보다 좁아져 prod 46건을 거짓 차단한다.
   *
   * ⇒ 이 prop은 **게이트를 싣지 않는 화면이 이 메뉴를 쓰게 될 때를 위한 심층 방어**로 남아 있다
   * (오늘 그런 소비처는 없다 — 스케줄 화면은 이 메뉴를 쓰지 않고 자기 렌더를 갖는다).
   * ⚠️ 그래서 **이 prop을 막는 spec의 초록을 「권한이 막힌다」의 근거로 인용하지 말 것** — 오늘
   * 실제로 권한을 막는 것은 서버 게이트다(그 축의 소유자는 `inspections.service.spec.ts`).
   */
  canGenerateReport?: boolean;
  /**
   * 「보고서 생성」 **서버 판정**(`resolveReportGate` — shared 정본).
   *
   * ⚠️ **2026-10-09부터 권한 축도 여기 들어 있다**(`a8061c0c4`) — 종전 주석은 「권한 축은 빠져
   * 있다」였고 그때는 서버가 `canCreateReport: true`를 넘겼다. 이제 `disabledReason`이 「권한이
   * 없다」까지 말한다.
   *
   * `undefined`면 폴백으로 종전 판정(`status === 'COMPLETED'`)만 본다 — 그 필드를 싣지 않는 응답
   * 경로가 있기 때문이다(계약 전문은 `lib/inspections.ts`의 이 필드 JSDoc). ⚠️ 그 폴백 가지는
   * **권한을 보지 않으므로**, 게이트 없는 화면이 이 메뉴를 쓰게 되면 위 `canGenerateReport`를
   * 반드시 넘겨야 한다.
   */
  reportGate?: { canGenerate: boolean; disabledReason: string | null };
  /** 검토 요청 로딩 상태 */
  isRequestingReview?: boolean;
  onView?: () => void;
  onGenerateReport?: () => void;
  onRequestReview?: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
  /** 트리거 결 교체 — 표 행에서는 공용 `ROW_ACTION_TRIGGER_CLASS`를 넘긴다. */
  triggerClassName?: string;
}

export function InspectionDropdownMenu({
  inspectionId: _inspectionId,
  status,
  isDeleted = false,
  canDelete = true,
  isLockedByLaterInspection = false,
  canRequestReview = false,
  canGenerateReport = false,
  reportGate,
  isRequestingReview = false,
  onView,
  onGenerateReport,
  onRequestReview,
  onDelete,
  onRestore,
  triggerClassName,
}: InspectionDropdownMenuProps) {
  const items: MenuItem[] = [];

  if (onView) {
    items.push({
      id: 'view',
      label: '상세 보기',
      icon: <EyeIcon className={MENU_ICON_CLASS} />,
      onClick: onView,
    });
  }

  // 검토 요청: 진행 중인 점검에서만 표시
  if (onRequestReview && !isDeleted && status === 'IN_PROGRESS') {
    items.push({
      id: 'request-review',
      label: isRequestingReview ? '요청 중...' : '검토 요청',
      icon: <ClipboardCheckIcon className={MENU_ICON_CLASS} />,
      onClick: onRequestReview,
      disabled: !canRequestReview || isRequestingReview,
      disabledReason: !canRequestReview ? '주 담당자 또는 조직 관리자만 요청할 수 있습니다.' : undefined,
    });
  }

  /**
   * 보고서 생성: 삭제되지 않은 점검에만 표시.
   *
   * ## 활성 조건 = **내 권한 ∧ 서버 게이트** — 한쪽만 보면 거짓 어포던스가 된다
   *
   * ⚠️⚠️ **이 절의 전제는 2026-10-09(`a8061c0c4`)에 바뀠다 — 아래 「축이 둘」은 그때까지의
   * 이야기다.** 지금은 **권한 축도 `reportGate`에 들어 있고**(서버가 `resolveCanCreateReport`로
   * 집행 식을 미러한다) 유일한 운영 소비처는 `canGenerateReport`에 **`true`를 넘긴다.** 즉 오늘의
   * 실질 판정은 **`reportGate` 하나**이고, AND는 게이트 없는 미래 소비처를 위한 심층 방어다.
   * 그 경위·근거(prod 46건 거짓 차단)는 `InspectionTable`의 `canModify` 주석이 정본이다.
   *
   * 축이 둘이고 **각자 소유자가 달랐다**(~2026-10-09):
   *  - **권한**(`canGenerateReport`) — 호출부가 계산했다. 서버 응답의 게이트엔 **없었다**(서버가
   *    `canCreateReport: true`를 넘겼다 — 목록 N건에 현장별 scope 조회가 N번 붙는 것을 피하려고).
   *  - **나머지 전부**(완료 가능성 · 개선대책 선택 1건 이상 · 참여자 전원 점검완료 서명) —
   *    `reportGate`. 정본은 shared `resolveReportGate`이고 상세 3 레이아웃도 **같은 함수**를 쓴다.
   *
   * ⚠️ 2026-10-08까지 이 자리가 **`COMPLETED` + 권한뿐**이었다. 그래서 완료·서명까지 끝난 뒤
   * 발견사항을 지워 선택이 0이 된 점검은 **상세에서는 막히고 목록에서는 열렸다**(같은 점검, 다른
   * 답). 식을 여기 손으로 다시 쓰지 말 것 — 래칫은 `lib/__tests__/reportGate.test.ts`(⑦~⑫)다.
   *
   * ⚠️ `reportGate`가 없으면 폴백은 **「막는 쪽」이 아니라 종전 판정**이다. 스케줄 응답처럼 그
   * 필드를 싣지 않는 경로가 있어, 없는 필드로 잠그면 그 화면의 기능이 통째로 꺼진다.
   *
   * ⚠️ **`status === 'COMPLETED'` 항은 게이트가 있을 때 «일부러» 빼 두었다 — 지우고 잊은 것이
   * 아니다.** 정본 게이트(shared `resolveReportGate`)는 status를 보지 않고 **완료의 재료**를 본다
   * (사진 1장 이상 + 개선대책 선택 + 참여자 전원 점검완료 서명). 상세 3 레이아웃도 같은 함수를
   * 같은 입력으로 부르므로, 여기에 `isCompleted`를 **AND로 더하면 목록이 상세보다 빡세져** 「같은
   * 점검, 다른 답」이 **반대 방향으로** 되살아난다(이 라운드가 없애는 그 결함이다). 실질적으로도
   * 중복이다 — 참여자 전원 서명이 차면 점검은 COMPLETED가 된다. 폴백 가지(`?? isCompleted`)에만
   * 남겨 둔 이유는 그쪽이 **게이트 없는 화면의 종전 판정**이기 때문이다.
   *
   * ## 사유는 **권한이 먼저**다
   *
   * 둘이 동시에 막을 수 있다. 그때 보여줄 것은 **사용자가 지금 할 수 있는 일을 가리키는 쪽**이고,
   * 권한은 본인이 바꿀 수 없으므로 「개선대책을 선택하세요」를 먼저 보이면 **할 수 없는 일**을
   * 가리킨다(그 거짓 안내가 이 라운드가 고치고 있는 결함의 유형 그 자체다).
   */
  if (onGenerateReport && !isDeleted) {
    const isCompleted = status === 'COMPLETED';
    const gateOpen = reportGate?.canGenerate ?? isCompleted;
    const isDisabled = !canGenerateReport || !gateOpen;
    let disabledReason: string | undefined;
    if (!canGenerateReport) {
      disabledReason = '보고서 생성은 주 담당자 또는 조직 관리자만 가능합니다.';
    } else if (reportGate) {
      // 서버가 사유를 소유한다 — `canGenerate === false ⟺ disabledReason !== null`이 그 모듈의
      // 불변식이라 `?? undefined`는 열린 경우에만 닿는다(그때는 `isDisabled`도 false).
      disabledReason = reportGate.disabledReason ?? undefined;
    } else if (!isCompleted) {
      disabledReason = '점검 완료 후 보고서를 생성할 수 있습니다.';
    }
    items.push({
      id: 'report',
      label: '보고서 생성',
      icon: <DocumentIcon className={MENU_ICON_CLASS} />,
      onClick: onGenerateReport,
      disabled: isDisabled,
      disabledReason,
    });
  }

  // 삭제된 점검: 복원 버튼 표시
  if (isDeleted && onRestore) {
    items.push({
      id: 'restore',
      label: '복원',
      icon: <ArrowUturnLeftIcon className={MENU_ICON_CLASS} />,
      onClick: onRestore,
      disabled: !canDelete,
      disabledReason: !canDelete ? '점검 생성자 또는 조직 관리자만 복원할 수 있습니다.' : undefined,
      divider: true,
    });
  } else if (onDelete) {
    // 활성 점검: 삭제 버튼 표시
    const isDeleteDisabled = !canDelete || isLockedByLaterInspection;
    let deleteDisabledReason: string | undefined;
    if (isLockedByLaterInspection) {
      deleteDisabledReason = '이후 차수의 점검이 존재하여 삭제할 수 없습니다.';
    } else if (!canDelete) {
      deleteDisabledReason = '점검 생성자 또는 조직 관리자만 삭제할 수 있습니다.';
    }
    items.push({
      id: 'delete',
      label: '삭제',
      icon: <TrashIcon className={MENU_ICON_CLASS} />,
      onClick: onDelete,
      danger: canDelete && !isLockedByLaterInspection,
      disabled: isDeleteDisabled,
      disabledReason: deleteDisabledReason,
      divider: true,
    });
  }

  return <DropdownMenu items={items} triggerClassName={triggerClassName} />;
}

/**
 * 사진 카드용 드롭다운 메뉴
 */
interface PhotoDropdownMenuProps {
  /** 사진 ID (향후 사용을 위해 예약) */
  photoId?: string;
  /** 발견사항 있음 여부 (향후 사용을 위해 예약) */
  hasFindings?: boolean;
  onViewFullsize?: () => void;
  onDownload?: () => void;
  onAddFinding?: () => void;
  onResolveHazard?: () => void;
  /** 위험요인 해소 비활성화 사유 */
  resolveHazardDisabled?: boolean;
  resolveHazardDisabledReason?: string;
  onReanalyze?: () => void;
  /** 분석 범위를 지정한 뒤 재분석 — 모달은 페이지가 띄운다(여기서는 대상만 지목한다) */
  onReanalyzeWithRegion?: () => void;
  onDelete?: () => void;
}

export function PhotoDropdownMenu({
  photoId: _photoId,
  hasFindings: _hasFindings,
  onViewFullsize,
  onDownload,
  onAddFinding,
  onResolveHazard,
  resolveHazardDisabled,
  resolveHazardDisabledReason,
  onReanalyze,
  onReanalyzeWithRegion,
  onDelete,
}: PhotoDropdownMenuProps) {
  const items: MenuItem[] = [];

  if (onViewFullsize) {
    items.push({
      id: 'fullsize',
      label: '그리기 메모',
      icon: <PencilIcon className={MENU_ICON_CLASS} />,
      onClick: onViewFullsize,
    });
  }

  if (onDownload) {
    items.push({
      id: 'download',
      label: '다운로드',
      icon: <ArrowDownTrayIcon className={MENU_ICON_CLASS} />,
      onClick: onDownload,
    });
  }

  if (onAddFinding) {
    items.push({
      id: 'add-finding',
      label: '발견사항 추가',
      icon: <PlusIcon className={MENU_ICON_CLASS} />,
      onClick: onAddFinding,
      divider: true,
    });
  }

  if (onResolveHazard) {
    items.push({
      id: 'resolve-hazard',
      label: '위험요인 해소',
      icon: <PlusIcon className={MENU_ICON_CLASS} />,
      onClick: onResolveHazard,
      disabled: resolveHazardDisabled,
      disabledReason: resolveHazardDisabledReason,
      divider: !onAddFinding,
    });
  }

  if (onReanalyze) {
    items.push({
      id: 'reanalyze',
      label: '다시 분석',
      icon: <ArrowPathIcon className={MENU_ICON_CLASS} />,
      onClick: onReanalyze,
      divider: !onAddFinding && !onResolveHazard,
    });
  }

  if (onReanalyzeWithRegion) {
    items.push({
      id: 'reanalyze-region',
      label: '범위 지정 후 다시 분석',
      icon: <ArrowPathIcon className={MENU_ICON_CLASS} />,
      onClick: onReanalyzeWithRegion,
    });
  }

  if (onDelete) {
    items.push({
      id: 'delete',
      label: '삭제',
      icon: <TrashIcon className={MENU_ICON_CLASS} />,
      onClick: onDelete,
      danger: true,
      divider: !onReanalyze && !onAddFinding && !onResolveHazard,
    });
  }

  return <DropdownMenu items={items} size="sm" />;
}

export default DropdownMenu;
